import CircularProgress from '@mui/material/CircularProgress'
import { useEffect, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { Navigate, useLocation } from 'react-router'

import { useRuntimeConfig } from '@/shared/config/runtime-config-context'

import { useAuth } from './auth-context'
import { useSignInRequired } from './public-page'
import { rememberReturnPath } from './return-path'
import { useSession } from './session'

/**
 * Renders children once the caller is signed in and holds this site's session. Signed out, it
 * redirects to /login; signed in with no session, to the auth callback, which starts one. A
 * convenience, not the protection: the API checks every request itself.
 */
export function RequireAuth(props: { readonly children: ReactNode }) {
  const intl = useIntl()
  const { isAuthenticated, isResolving } = useAuth()
  const { status } = useSession()
  const { redirectPath } = useRuntimeConfig().auth
  const location = useLocation()
  useSignInRequired()

  // Not `!isAuthenticated` alone: during startup that would overwrite a real destination.
  const isRedirecting = !isResolving && status === 'signedOut'
  const { pathname, search, hash } = location

  // An effect, because React renders more than once. The hash keeps in-page deep links working.
  useEffect(() => {
    if (isRedirecting) {
      rememberReturnPath(`${pathname}${search}${hash}`)
    }
  }, [isRedirecting, pathname, search, hash])

  // Wait, don't redirect: replacing to /login during startup loses the requested address.
  if (isResolving || status === 'checking') {
    return (
      <div className="flex flex-1 items-center justify-center py-24">
        <CircularProgress aria-label={intl.formatMessage({ id: 'shell.auth.checkingSignIn' })} />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (status === 'signedOut') {
    return <Navigate to={redirectPath} replace />
  }

  return props.children
}
